
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CategorySelect } from "@/components/calculadora/equipamento/CategorySelect";
import { ModelSelect } from "@/components/calculadora/equipamento/ModelSelect";
import { DetailsFields } from "@/components/calculadora/equipamento/DetailsFields";
import { EquipamentoSolar } from "@/types";
import { useState, useEffect } from "react";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

interface PainelFormProps {
  marcasPaineis: string[];
  modelosPorMarca: Record<string, string[]>;
  marcaSelecionada: string;
  setMarcaSelecionada: (value: string) => void;
  modeloSelecionado: string;
  setModeloSelecionado: (value: string) => void;
  itemPersonalizado: boolean;
  setItemPersonalizado: (value: boolean) => void;
  detalhesEquipamento: any;
  setDetalhesEquipamento: (value: any) => void;
  editandoEquipamento: EquipamentoSolar | null;
  handleAdicionarEquipamento: () => void;
}

export const PainelForm = ({
  marcasPaineis,
  modelosPorMarca,
  marcaSelecionada,
  setMarcaSelecionada,
  modeloSelecionado,
  setModeloSelecionado,
  itemPersonalizado,
  setItemPersonalizado,
  detalhesEquipamento,
  setDetalhesEquipamento,
  editandoEquipamento,
  handleAdicionarEquipamento
}: PainelFormProps) => {
  const [isAddingMarca, setIsAddingMarca] = useState(false);
  const [novaMarca, setNovaMarca] = useState("");
  const [todasMarcas, setTodasMarcas] = useState<string[]>(marcasPaineis);
  const [isAddingModelo, setIsAddingModelo] = useState(false);
  const [novoModelo, setNovoModelo] = useState("");
  const [todosModelos, setTodosModelos] = useState<Record<string, string[]>>(modelosPorMarca);

  // Sincronizar com localStorage na inicialização
  useEffect(() => {
    const marcasSalvas = localStorage.getItem('marcasPaineis');
    const modelosSalvos = localStorage.getItem('modelosPaineis');
    
    if (marcasSalvas) {
      const marcasCarregadas = JSON.parse(marcasSalvas);
      setTodasMarcas(marcasCarregadas);
    }
    
    if (modelosSalvos) {
      const modelosCarregados = JSON.parse(modelosSalvos);
      setTodosModelos(modelosCarregados);
    }
  }, []);

  // Enviar dados atualizados para o componente pai
  useEffect(() => {
    // Atualizar apenas se houver alterações
    if (JSON.stringify(todasMarcas) !== JSON.stringify(marcasPaineis)) {
      const marcasAtualizadas = [...todasMarcas];
      localStorage.setItem('marcasPaineis', JSON.stringify(marcasAtualizadas));
      
      // Atualizar modelo global no calculadora
      const paineisCadastrados = localStorage.getItem('paineisCadastrados');
      if (paineisCadastrados) {
        try {
          const paineis = JSON.parse(paineisCadastrados);
          localStorage.setItem('paineisCadastrados', JSON.stringify(paineis));
        } catch (error) {
          console.error("Erro ao atualizar painéis cadastrados:", error);
        }
      }
    }
  }, [todasMarcas, marcasPaineis]);

  // Sincronizar modelos com localStorage
  useEffect(() => {
    if (JSON.stringify(todosModelos) !== JSON.stringify(modelosPorMarca)) {
      localStorage.setItem('modelosPaineis', JSON.stringify(todosModelos));
    }
  }, [todosModelos, modelosPorMarca]);

  const handleAddMarca = () => {
    if (!novaMarca.trim()) {
      toast.error("Digite o nome da marca");
      return;
    }

    if (todasMarcas.includes(novaMarca)) {
      toast.error("Esta marca já existe");
      return;
    }

    const novasMarcas = [...todasMarcas, novaMarca];
    setTodasMarcas(novasMarcas);
    
    // Inicializar a marca com array vazio de modelos
    const novosModelos = { ...todosModelos, [novaMarca]: [] };
    setTodosModelos(novosModelos);
    
    // Salvar no localStorage para persistência
    try {
      localStorage.setItem('marcasPaineis', JSON.stringify(novasMarcas));
      localStorage.setItem('modelosPaineis', JSON.stringify(novosModelos));
      toast.success(`Marca ${novaMarca} adicionada com sucesso`);
      setMarcaSelecionada(novaMarca);
      setIsAddingMarca(false);
      setNovaMarca("");
    } catch (error) {
      console.error("Erro ao salvar marca:", error);
      toast.error("Erro ao salvar nova marca");
    }
  };

  const handleCancelAddMarca = () => {
    setIsAddingMarca(false);
    setNovaMarca("");
  };
  
  const handleAddModelo = () => {
    if (!novoModelo.trim()) {
      toast.error("Digite o nome do modelo");
      return;
    }
    
    if (!marcaSelecionada) {
      toast.error("Selecione uma marca primeiro");
      return;
    }
    
    const modelosAtuais = todosModelos[marcaSelecionada] || [];
    
    if (modelosAtuais.includes(novoModelo)) {
      toast.error("Este modelo já existe para esta marca");
      return;
    }
    
    const novosModelos = { 
      ...todosModelos, 
      [marcaSelecionada]: [...modelosAtuais, novoModelo] 
    };
    
    setTodosModelos(novosModelos);
    
    // Salvar no localStorage para persistência e compartilhar com a calculadora
    try {
      localStorage.setItem('modelosPaineis', JSON.stringify(novosModelos));
      
      // Adicionar como equipamento disponível para a calculadora
      const paineisCadastrados = localStorage.getItem('paineisCadastrados');
      let paineis = [];
      
      if (paineisCadastrados) {
        paineis = JSON.parse(paineisCadastrados);
      }
      
      // Verificar se já existe um painel similar cadastrado
      const painelExistente = paineis.find(
        (p: any) => p.marca === marcaSelecionada && p.modelo === novoModelo
      );
      
      if (!painelExistente) {
        paineis.push({
          id: crypto.randomUUID(), 
          tipo: "painel",
          marca: marcaSelecionada,
          modelo: novoModelo,
          potencia: detalhesEquipamento.potencia || 0,
          precoUnitario: detalhesEquipamento.precoUnitario || 0,
          disponibilidade: true
        });
        
        localStorage.setItem('paineisCadastrados', JSON.stringify(paineis));
      }
      
      toast.success(`Modelo ${novoModelo} adicionado com sucesso`);
      setModeloSelecionado(novoModelo);
      setIsAddingModelo(false);
      setNovoModelo("");
    } catch (error) {
      console.error("Erro ao salvar modelo:", error);
      toast.error("Erro ao salvar novo modelo");
    }
  };
  
  const handleCancelAddModelo = () => {
    setIsAddingModelo(false);
    setNovoModelo("");
  };

  return (
    <div>
      <h3 className="font-semibold mb-4">Cadastro de Painel Solar</h3>
      
      <div className="space-y-4">
        {!isAddingMarca ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="marca">Marca</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setIsAddingMarca(true)}
                className="h-8 px-2 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nova Marca
              </Button>
            </div>
            <CategorySelect 
              categoria={marcaSelecionada}
              setCategoria={setMarcaSelecionada}
              categorias={todasMarcas.map(marca => ({ marca, modelos: todosModelos[marca] || [] }))}
              label=""
              placeholder="Selecione a marca do painel"
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="novaMarca">Nova Marca</Label>
            <div className="flex gap-2">
              <Input
                id="novaMarca"
                value={novaMarca}
                onChange={(e) => setNovaMarca(e.target.value)}
                placeholder="Nome da nova marca"
                className="flex-1"
              />
              <Button 
                type="button" 
                size="sm"
                onClick={handleAddMarca}
                className="px-2"
              >
                <Check className="h-4 w-4 mr-1" />
                Salvar
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleCancelAddMarca}
                className="px-2"
              >
                <X className="h-4 w-4 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        )}
        
        {marcaSelecionada && !itemPersonalizado && !isAddingModelo && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="modelo">Modelo</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setIsAddingModelo(true)}
                className="h-8 px-2 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Novo Modelo
              </Button>
            </div>
            <ModelSelect 
              modelo={modeloSelecionado}
              setModelo={setModeloSelecionado}
              setPersonalizado={setItemPersonalizado}
              categorias={todasMarcas.map(marca => ({ marca, modelos: todosModelos[marca] || [] }))}
              categoria={marcaSelecionada}
              label=""
              placeholder="Selecione o modelo do painel"
            />
          </div>
        )}
        
        {marcaSelecionada && isAddingModelo && (
          <div className="space-y-2">
            <Label htmlFor="novoModelo">Novo Modelo para {marcaSelecionada}</Label>
            <div className="flex gap-2">
              <Input
                id="novoModelo"
                value={novoModelo}
                onChange={(e) => setNovoModelo(e.target.value)}
                placeholder="Nome do novo modelo"
                className="flex-1"
              />
              <Button 
                type="button" 
                size="sm"
                onClick={handleAddModelo}
                className="px-2"
              >
                <Check className="h-4 w-4 mr-1" />
                Salvar
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleCancelAddModelo}
                className="px-2"
              >
                <X className="h-4 w-4 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        )}
        
        {marcaSelecionada && itemPersonalizado && (
          <div className="grid gap-2">
            <div className="flex justify-between">
              <Label htmlFor="modeloPersonalizado">Modelo Personalizado</Label>
              <Button 
                type="button" 
                variant="link" 
                className="text-xs h-6 p-0"
                onClick={() => {
                  setItemPersonalizado(false);
                  setModeloSelecionado("");
                }}
              >
                Selecionar Modelo Existente
              </Button>
            </div>
            <Input 
              id="modeloPersonalizado" 
              value={modeloSelecionado}
              onChange={(e) => setModeloSelecionado(e.target.value)}
              placeholder="Nome do modelo (ex: CS3N-395MS)"
            />
          </div>
        )}
        
        {(marcaSelecionada && (modeloSelecionado || itemPersonalizado)) && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="potencia">Potência (W)</Label>
                <Input
                  id="potencia"
                  type="number"
                  min="1"
                  value={detalhesEquipamento.potencia || ""}
                  onChange={(e) => setDetalhesEquipamento({
                    ...detalhesEquipamento,
                    potencia: Number(e.target.value) || 0
                  })}
                  placeholder="Ex: 400"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="precoUnitario">Preço Unitário (R$)</Label>
                <Input
                  id="precoUnitario"
                  type="number"
                  min="0"
                  step="0.01"
                  value={detalhesEquipamento.precoUnitario || ""}
                  onChange={(e) => setDetalhesEquipamento({
                    ...detalhesEquipamento,
                    precoUnitario: Number(e.target.value) || 0
                  })}
                  placeholder="Ex: 800.00"
                />
              </div>
            </div>
            
            <DetailsFields
              detalhes={detalhesEquipamento}
              setDetalhes={setDetalhesEquipamento}
              showQuantidadeCampos={false}
            />
            
            <Button 
              onClick={handleAdicionarEquipamento}
              className="w-full"
            >
              {editandoEquipamento ? "Atualizar Painel" : "Adicionar Painel"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
